import { createSlice } from '@reduxjs/toolkit'
import { tokenStorage } from 'Modules/API'
import i18n from '../../i18n'

export const initialState = {
    currentUser: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    qrLoading: false,
    defaultCard: 'Work',
    sortContacts: 'Family Name',
    entryData: null,
    qrCardName: 'Work',
    verificationEmail: { loading: false, sent: false, error: null },
    // moved from parseSlice
    connections: { outgoing: [], incoming: [] }
}

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        loginRequest(state) { state.loading = true; state.error = null; state.isAuthenticated = false },
        loginSuccess(state, action) { state.loading = false; state.error = null; state.currentUser = action.payload; state.isAuthenticated = true },
        loginFailure(state, action) { state.loading = false; state.error = action.payload; state.currentUser = null; state.isAuthenticated = false },
        logoutRequest(state) { state.loading = true },
        logoutSuccess() { return initialState },
        logoutFailure() { return initialState },
        logoutUser(state) {
            // Clear token from localStorage
            localStorage.removeItem('HMI_AUTH_TOKEN')
            // Return new state instead of modifying draft
            return initialState
        },
        refetchUserRequest(state) { state.loading = true },
        refetchUserSuccess(state, action) { 
            state.loading = false; 
            // IMPORTANT: Create a new object reference to ensure React detects the change
            // This is critical for components that depend on currentUser.profile
            state.currentUser = { ...action.payload };
            // If profile exists, ensure it's also a new reference and exclude Public card
            if (state.currentUser.profile) {
                state.currentUser.profile = { 
                    ...state.currentUser.profile,
                    cards: state.currentUser.profile.cards ? state.currentUser.profile.cards.filter(c => (c.name || '').toLowerCase() !== 'public') : [],
                    properties: state.currentUser.profile.properties ? [...state.currentUser.profile.properties] : []
                };
            }
            state.isAuthenticated = true;
        },
        refetchUserFailure(state, action) { state.loading = false; state.error = action.payload },
        cleanError(state) { state.error = null },
        triggerLoading(state) { if (!state.loading) state.loading = true },
        checkTokenValidity(state, action) { state.isAuthenticated = action.payload; state.currentUser = action.payload ? state.currentUser : null },
        updateProfile(state, action) {
            if (state.currentUser) {
                const prof = action.payload || {};
                state.currentUser = {
                    ...state.currentUser,
                    profile: {
                        ...prof,
                        cards: prof.cards ? prof.cards.filter(c => (c.name || '').toLowerCase() !== 'public') : []
                    }
                };
            }
        },
        // moved from parseSlice
        getUserRequest(state) { state.loading = true; state.error = null },
        getUserSuccess(state, action) {
            try {
                const names = Array.isArray(action.payload.names) ? action.payload.names : []
                const namesMap = new Map(names.map(user => {
                    try {
                        if (user && user.objectId && user.profile && user.profile.properties) {
                            const nameProperty = user.profile.properties
                                .filter(property => property && (property.name === 'givenName' || property.name === 'familyName'))
                                .map(property => property.value)
                                .join(' ')
                            return [user.objectId, nameProperty]
                        }
                        return [user.objectId, 'Unknown']
                    } catch (error) { return [user.objectId, 'Unknown'] }
                }))
                const connections = Array.isArray(action.payload.connections) ? action.payload.connections : []
                const byDir = connections.reduce((res, connection) => {
                    try {
                        if (connection && connection.fromPerson && connection.toPerson) {
                            if (connection.fromPerson.objectId === action.payload.user.objectId) {
                                if (namesMap.has(connection.toPerson.objectId)) connection.fullName = namesMap.get(connection.toPerson.objectId)
                                res.outgoing.push(connection)
                            } else {
                                if (namesMap.has(connection.fromPerson.objectId)) connection.fullName = namesMap.get(connection.fromPerson.objectId)
                                res.incoming.push(connection)
                            }
                        }
                    } catch (_) { }
                    return res
                }, { outgoing: [], incoming: [] })
                state.loading = false
                state.error = null
                state.connections = byDir
            } catch (error) {
                state.loading = false
                state.error = error.message
                state.connections = { outgoing: [], incoming: [] }
            }
        },
        getUserFailure(state, action) { state.loading = false; state.error = action.payload?.error || action.payload }
    }
})

export const authActions = authSlice.actions
export default authSlice.reducer

// Re-export existing async thunks from the legacy file for compatibility
// export * from '../authSlice' // Removed circular import

// Back-compat shims for legacy Actions/AuthActions named exports
export const login = (email, password) => async (dispatch) => {
    dispatch(authActions.loginRequest())
    console.log('Login attempt with:', { email, password })
    const API_BASE = window.HMI_API_BASE || process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api'

    try {
        const res = await fetch(`${API_BASE}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        })
        console.log('Login response status:', res.status)

        if (!res.ok) {
            let err = {}
            try { err = await res.json() } catch(e) {}
            throw new Error(err.error?.message || 'Login failed')
        }

        const data = await res.json()
        console.log('Login success response:', data)

        if (data.success) {
            localStorage.setItem('HMI_AUTH_TOKEN', data.data.token)
            dispatch(authActions.loginSuccess(data.data.user))
            return data.data.user
        } else {
            throw new Error(data.error?.message || 'Login failed')
        }
    } catch (error) {
        console.log('Login catch error:', error)
        dispatch(authActions.loginFailure(error.message))
        // Deliberately NOT throwing here to prevent Unhandled Rejections in UI
    }
}
export const logout = () => (dispatch) => {
    dispatch(authActions.logoutUser())
    return Promise.resolve() // Return a resolved promise for .catch() compatibility
}
export const cleanError = () => authActions.cleanError()
export const sendVerificationEmail = () => (dispatch) => Promise.reject(new Error('Not implemented'))
export const initPasswordRecovery = () => (dispatch) => Promise.reject(new Error('Not implemented'))
export const finishPasswordRecovery = () => (dispatch) => Promise.reject(new Error('Not implemented'))
export const getParseTokenFromOAuth = () => (dispatch) => Promise.reject(new Error('Deprecated'))
export const setUserFromToken = () => (dispatch) => Promise.reject(new Error('Deprecated'))
export const initUserDeletion = () => (dispatch) => Promise.reject(new Error('Not implemented'))
export const finishUserDeletion = () => (dispatch) => Promise.reject(new Error('Not implemented'))

// Add missing exports that components are trying to import
export const refetchUser = () => (dispatch) => {
    dispatch(authActions.refetchUserRequest())

    // Use window.HMI_API_BASE if available (for runtime configuration), otherwise fall back to env or default
    const API_BASE = window.HMI_API_BASE || process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api'
    const token = localStorage.getItem('HMI_AUTH_TOKEN')

    if (!token) {
        dispatch(authActions.refetchUserFailure('No token found'))
        return Promise.reject(new Error('No token found'))
    }

    // Add cache-busting parameter to prevent browser/HTTP caching
    // Note: Cache-Control headers are response headers, not request headers
    // The query parameters are sufficient to prevent caching
    const cacheBuster = `?t=${Date.now()}&_=${Math.random().toString(36).substr(2, 9)}`
    console.log('🔄 [refetchUser] Fetching profile from:', `${API_BASE}/users/profile${cacheBuster}`)
    return fetch(`${API_BASE}/users/profile${cacheBuster}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    })
        .then(res => {
            if (res.status === 401) {
                // Token expired or invalid, clear auth state
                localStorage.removeItem('HMI_AUTH_TOKEN')
                dispatch(authActions.logoutUser())
                // Redirect to login
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login'
                }
                throw new Error('Token expired')
            }
            return res.json()
        })
        .then(data => {
            if (data.success) {
                // Merge profile into user object if it exists in the response
                // IMPORTANT: Create a new object reference to ensure React detects the change
                const userWithProfile = data.profile 
                    ? { 
                        ...data.user, 
                        profile: {
                            ...data.profile,
                            // Ensure properties array is a new reference
                            properties: data.profile.properties ? [...data.profile.properties] : [],
                            mergedContacts: data.profile.mergedContacts || data.user?.mergedContacts || []
                        },
                        mergedContacts: data.profile.mergedContacts || data.user?.mergedContacts || []
                    }
                    : data.user
                console.log('✅ [refetchUser] Profile fetched successfully:', {
                    userId: userWithProfile.id,
                    profilePropertiesCount: userWithProfile.profile?.properties?.length || 0,
                    profileCardsCount: userWithProfile.profile?.cards?.length || 0,
                    propertyNames: userWithProfile.profile?.properties?.map(p => p.name) || []
                })
                
                // Sync language from backend
                if (userWithProfile.language && i18n.language !== userWithProfile.language) {
                    i18n.changeLanguage(userWithProfile.language).catch(err => console.error('Failed to sync language', err));
                }

                dispatch(authActions.refetchUserSuccess(userWithProfile))
                return userWithProfile
            } else {
                throw new Error(data.error?.message || 'Failed to fetch user')
            }
        })
        .catch(error => {
            dispatch(authActions.refetchUserFailure(error.message))
            throw error
        })
}
export const checkTokenValidity = () => (dispatch) => {
    return new Promise((resolve) => {
        // Actually validate token with server
        const token = localStorage.getItem('HMI_AUTH_TOKEN')
        if (!token) {
            dispatch(authActions.checkTokenValidity(false))
            resolve(false)
            return
        }

        const API_BASE = window.HMI_API_BASE || process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api'
        fetch(`${API_BASE}/users/profile`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        })
        .then(res => {
            const isValid = res.ok
            dispatch(authActions.checkTokenValidity(isValid))
            if (!isValid) {
                // Token is invalid, clear it
                localStorage.removeItem('HMI_AUTH_TOKEN')
            }
            resolve(isValid)
        })
        .catch(error => {
            console.error('Token validation error:', error)
            dispatch(authActions.checkTokenValidity(false))
            localStorage.removeItem('HMI_AUTH_TOKEN')
            resolve(false)
        })
    })
}

// Sign up user via new API (migrated from legacy Actions)
export const signUp = (userInfo) => (dispatch) => {
    const API_BASE = window.HMI_API_BASE || process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api'
    const { email, password, firstName, lastName, phone } = userInfo
    const username = email
    return fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, username, password, firstName, lastName, phone })
    })
        .then(res => {
            if (!res.ok) {
                return res.json().then(err => Promise.reject(new Error(err.error?.message || 'Registration failed')))
            }
            return res.json()
        })
        .then(data => {
            if (data.success) {
                tokenStorage.setAuthToken(data.data.token)
                dispatch(authActions.loginSuccess(data.data.user))
                return data.data.user
            } else {
                throw new Error(data.error?.message || 'Registration failed')
            }
        })
}

